import React, { useEffect, useState } from 'react';
import { Box, keyframes } from '@mui/material';
import CollectionsIcon from '@mui/icons-material/Collections';
import DescriptionIcon from '@mui/icons-material/Description';

// Flash and fade animation
const flashFade = keyframes`
  0% {
    transform: scale(0) rotate(0deg);
    opacity: 0;
  }
  20% {
    transform: scale(1.2) rotate(10deg);
    opacity: 1;
  }
  40% {
    transform: scale(1) rotate(-5deg);
    opacity: 1;
  }
  60% {
    transform: scale(1.1) rotate(0deg);
    opacity: 1;
  }
  100% {
    transform: scale(0.8) rotate(0deg);
    opacity: 0;
  }
`;

// Pulsing glow
const pulseGlow = keyframes`
  0%, 100% {
    box-shadow: 0 0 20px rgba(255, 215, 0, 0.6);
  }
  50% {
    box-shadow: 0 0 40px rgba(255, 215, 0, 0.9);
  }
`;

/**
 * FoundItemFlash - Briefly flashes the found item (collectible or note)
 * @param {object} location - { lat, lng } where item was found
 * @param {string} itemType - 'collectible' or 'note'
 * @param {boolean} active - Whether animation is active
 * @param {object} mapRef - Reference to map via { getMap: () => map }
 * @param {function} onComplete - Callback when animation completes
 */
const FoundItemFlash = ({ location, itemType = 'collectible', active, mapRef, onComplete }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!active || !location || !mapRef?.getMap) return;

    const map = mapRef.getMap();
    if (!map) return;

    // Custom overlay for precise positioning
    class ItemOverlay extends window.google.maps.OverlayView {
      constructor(position) {
        super();
        this.position = position;
        this.div = null;
      }

      onAdd() {
        const div = document.createElement('div');
        div.style.position = 'absolute';
        div.style.pointerEvents = 'none';
        this.div = div;

        const panes = this.getPanes();
        panes.overlayLayer.appendChild(div);

        setVisible(true);
      }

      draw() {
        if (!this.div) return;

        const projection = this.getProjection();
        const point = projection.fromLatLngToDivPixel(
          new window.google.maps.LatLng(this.position.lat, this.position.lng)
        );

        if (point) {
          this.div.style.left = point.x + 'px';
          this.div.style.top = point.y + 'px';
        }
      }

      onRemove() {
        if (this.div) {
          this.div.parentNode.removeChild(this.div);
          this.div = null;
        }
      }
    }

    const overlay = new ItemOverlay(location);
    overlay.onRemove = function() {};
    overlay.setMap(map);

    // Auto-hide after 1.5 seconds
    const timer = setTimeout(() => {
      setVisible(false);
      try {
        overlay.setMap(null);
      } catch (e) {
        // Ignore cleanup errors
      }
      if (onComplete) {
        onComplete();
      }
    }, 1500);

    return () => {
      clearTimeout(timer);
      try {
        overlay.setMap(null);
      } catch (e) {
        // Ignore cleanup errors
      }
    };
  }, [active, location, mapRef, onComplete]);

  if (!visible) return null;

  const ItemIcon = itemType === 'collectible' ? CollectionsIcon : DescriptionIcon;
  const iconColor = itemType === 'collectible' ? '#FFD700' : '#4CAF50';

  return (
    <Box
      sx={{
        position: 'absolute',
        transform: 'translate(-50%, -50%)',
        pointerEvents: 'none',
        zIndex: 1000,
      }}
    >
      {/* Glowing background */}
      <Box
        sx={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${iconColor}40 0%, transparent 70%)`,
          animation: `${pulseGlow} 1.5s ease-in-out`,
        }}
      />

      {/* Item icon */}
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          border: `3px solid ${iconColor}`,
          animation: `${flashFade} 1.5s ease-out`,
        }}
      >
        <ItemIcon
          sx={{
            fontSize: '36px',
            color: iconColor,
          }}
        />
      </Box>
    </Box>
  );
};

export default FoundItemFlash;
